/**
 * Phase 8: 収入減少フェーズ（Puerto Rico 専用）
 *
 * 共通ロジックは通常版と同じだが、次フェーズが異なる:
 * - 通常版: next = 'refillGoods' (Phase 9)
 * - Puerto Rico: next = 'advanceTurn' (Phase 10, Phase 9をスキップ)
 *
 * @see docs/specifications/logic/common/phase_08_income_reduction.md
 * @see docs/specifications/logic/puerto_rico/phase_09_skip.md
 */

import type { PhaseConfig } from 'boardgame.io';
import { debug } from '../../utils/debug';
import type { PuertoRicoGameState } from '../../types';
import { getIncomeReduction } from '../../types';
import { undoToTurnStart } from '../moves/undoToTurnStart';

export const incomeReductionPuertoRicoPhase: PhaseConfig<PuertoRicoGameState> = {
  // Phase開始時に自動実行
  onBegin: ({ G }: { G: PuertoRicoGameState }) => {
    debug.log('[incomeReductionPuertoRicoPhase] ★★★ Phase 8: Income Reduction started ★★★');

    // ソロプレイ用: Player 0の収入減少
    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      // 倒産時は即座に次フェーズへ（結果表示なし）
      G.autoPhaseResult = null;
      return;
    }

    // 減少幅を計算
    const reduction = getIncomeReduction(player.incomeLevel);

    // 収入レベルを減少
    const oldIncomeLevel = player.incomeLevel;
    player.incomeLevel -= reduction;

    debug.log(`[incomeReductionPuertoRicoPhase] Player 0 incomeLevel: ${oldIncomeLevel} → ${player.incomeLevel} (reduction: ${reduction})`);

    // 収入レベルが0を下回った場合（負の場合）は破産
    if (player.incomeLevel < 0) {
      player.isBankrupt = true;
      G.isGameOver = true;
      G.endReason = 'BANKRUPTCY';
      debug.log('[incomeReductionPuertoRicoPhase] ★ Player BANKRUPT - Income dropped below 0');

      // 破産の処理結果を保存
      G.autoPhaseResult = {
        phase: 'incomeReduction',
        message: 'BANKRUPT! Income dropped to 0.',
        floatingText: `BANKRUPT!`,
        details: { reduction, bankrupt: true },
      };
    } else {
      // 処理結果を保存（UI表示用）
      G.autoPhaseResult = {
        phase: 'incomeReduction',
        message: 'Reducing income based on current level...',
        floatingText: reduction > 0 ? `-${reduction} Income` : 'No reduction',
        details: { reduction },
      };
    }

    debug.log('[incomeReductionPuertoRicoPhase] ★ Auto-processing complete, waiting for user click');
  },

  moves: {
    // ユーザークリックでフェーズ進行
    proceedAutoPhase: ({ G, events }: { G: PuertoRicoGameState; events: any }) => {
      G.autoPhaseResult = null;
      events.endPhase();
    },
    undoToTurnStart: undoToTurnStart as any,
  },

  // 倒産プレイヤーは自動的に次フェーズへ、または Undo 強制遷移
  endIf: ({ G }: { G: PuertoRicoGameState }) => {
    // Undo 強制遷移フラグが立っている場合は即座に終了
    if (G.forcePhaseToIssueShares) {
      debug.log('[incomeReductionPuertoRicoPhase] Force transition to issueShares (Undo)');
      return true;
    }

    const player = G.players['0'];
    if (!player || player.isBankrupt) {
      return true;
    }
    return false;
  },

  onEnd: ({ G }: { G: PuertoRicoGameState }) => {
    debug.log('[incomeReductionPuertoRicoPhase] ★★★ onEnd called - Phase 8 ended ★★★');
    debug.log('[incomeReductionPuertoRicoPhase] ★★★ Next phase: advanceTurn (Phase 9 skipped)');

    // 自動フェーズ履歴に追加（UIアニメーション用）
    if (!G.completedAutoPhases) {
      G.completedAutoPhases = [];
    }
    G.completedAutoPhases.push('incomeReduction');
  },

  // Puerto Rico: Phase 9（商品補充）をスキップして Phase 10（ターン進行）へ、または Undo 時は issueShares へ
  next: ({ G }: { G: PuertoRicoGameState }) => {
    if (G.forcePhaseToIssueShares) {
      return 'issueShares';
    }
    return 'advanceTurn';
  }
};
